import { calendarDate, momentDate } from '../dates';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { activeOrganizationId, graphql } from '../api/client';
import { confirmThenCollect, stripeClient } from '../api/payments';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader } from '../components/Workspace';

type PaymentMethod = { externalId: string; brand: string; last4: string; expiryMonth: number; expiryYear: number; isDefault: boolean };
type Plan = { code: string; name: string; monthlyPriceCents: number; includedNoticeCredits: number; maxUsers: number | null; selfServe: boolean };
type Charge = {
  id: string; kind: string; amountCents: number; description: string; status: string;
  dueAt: string; escalateAt: string; deadlineAt: string | null; attemptCount: number;
  lastFailureCode: string | null; lastFailureMessage: string | null; settledAt: string | null;
};
type Summary = { paymentAdapter: string; planCode: string; availableCredits: number; balanceCents: number };
type Subscription = {
  planCode: string; status: string; currentPeriodStart: string; currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean; pendingPlanCode: string | null; includedCreditsGranted: number;
};

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const title = (value: string) => value.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());


const BILLING_QUERY = `query Billing {
  billingSummary { planCode availableCredits balanceCents paymentAdapter }
  paymentMethods { externalId brand last4 expiryMonth expiryYear isDefault }
  planOptions { code name monthlyPriceCents includedNoticeCredits maxUsers selfServe }
  subscription { planCode status currentPeriodStart currentPeriodEnd cancelAtPeriodEnd pendingPlanCode includedCreditsGranted }
  scheduledCharges { id kind amountCents description status dueAt escalateAt deadlineAt attemptCount lastFailureCode lastFailureMessage settledAt }
}`;

export function BillingPage() {
  return <StandardBillingPage />;
}

function StandardBillingPage() {
  const { token } = useAuth();
  const [data, setData] = useState<{ billingSummary: Summary; paymentMethods: PaymentMethod[]; planOptions: Plan[]; scheduledCharges: Charge[]; subscription: Subscription | null } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [version, setVersion] = useState(0);
  const reload = () => setVersion((value) => value + 1);

  useEffect(() => {
    if (!token) return;
    void graphql<typeof data>({ query: BILLING_QUERY, token, organizationId: activeOrganizationId() })
      .then((value) => { setData(value); setError(''); })
      .catch((reason: Error) => setError(reason.message));
  }, [token, version]);

  async function run(label: string, query: string, variables?: Record<string, unknown>) {
    if (!token) return false;
    setBusy(label);
    setError('');
    try {
      await graphql({ query, ...(variables ? { variables } : {}), token, organizationId: activeOrganizationId() });
      reload();
      return true;
    } catch (reason) {
      setError((reason as Error).message);
      return false;
    } finally {
      setBusy('');
    }
  }

  async function payNow(id:string) {
    if (!token) return;
    setBusy('pay'); setError('');
    const organizationId=activeOrganizationId();
    const collect=()=>graphql<{payChargeNow:{status:string;clientSecret:string|null}}>({
      query:'mutation P($id:ID!){payChargeNow(chargeId:$id){id status clientSecret}}',
      variables:{id},token,organizationId
    });
    try {
      const result=await collect();
      if(result.payChargeNow.clientSecret && result.payChargeNow.status==='REQUIRES_ACTION') await confirmThenCollect(result.payChargeNow.clientSecret, collect);
    } catch(reason) {setError((reason as Error).message);} finally {setBusy('');reload();}
  }

  const summary = data?.billingSummary;
  const plan = data?.planOptions.find((option) => option.code === summary?.planCode);
  // What a customer most needs to see: money that has not been collected while a
  // notice deadline is approaching.
  const needsAttention = (data?.scheduledCharges ?? []).filter((charge) => ['FAILED', 'ESCALATED', 'REQUIRES_ACTION'].includes(charge.status));

  if (summary?.paymentAdapter === 'disabled') return <>
    <PageHeader eyebrow="Plan and billing" title="Billing" />
    <p>Payment collection and plan changes are not available yet. Notice credits can still cover an order in full.</p>
    {error && <div className="alert error" role="alert">{error}</div>}
    <section className="metric-grid four">
      <article><span>Notice credits left</span><strong>{summary.availableCredits}</strong></article>
      <article><span>Unpaid balance</span><strong>{money(summary.balanceCents)}</strong></article>
    </section>
    <PromotionPanel busy={busy} onRedeem={(code) => run('promotion', 'mutation Promo($code:String!){redeemPromotionCode(code:$code){id code noticeCredits redeemedAt}}', { code })} />
    <ChargesPanel charges={data?.scheduledCharges ?? []} busy={busy} onPayNow={payNow} />
  </>;

  return <>
    <PageHeader eyebrow="Plan and billing" title="Cards, charges, and what you have paid" />
    <p className="workspace-intro">
      Notices are charged ahead of their deadline so a payment problem can be fixed before a notice is late.
      A notice credit covers one notice included with your plan. Postage, delivery, and filing costs are separate.
    </p>
    {error && <div className="alert error" role="alert">{error}</div>}

    {needsAttention.length > 0 && <div className="alert error" role="alert">
      <strong>{needsAttention.length} charge{needsAttention.length === 1 ? '' : 's'} need attention.</strong>{' '}
      Review the charges below to update your card or complete payment authentication.
    </div>}

    <section className="metric-grid four">
      <article><span>Plan</span><strong>{plan?.name ?? title(summary?.planCode ?? '—')}</strong></article>
      <article><span>Notice credits left</span><strong>{summary?.availableCredits ?? '—'}</strong></article>
      <article><span>Unpaid balance</span><strong>{summary ? money(summary.balanceCents) : '—'}</strong></article>
      <article><span>Cards on file</span><strong>{data?.paymentMethods.length ?? '—'}</strong></article>
    </section>

    <PromotionPanel busy={busy} onRedeem={(code) => run('promotion', 'mutation Promo($code:String!){redeemPromotionCode(code:$code){id code noticeCredits redeemedAt}}', { code })} />

    <CardsPanel
      methods={data?.paymentMethods ?? []}
      busy={busy}
      onReload={reload}
      onCardAdded={reload}
      onChooseDefault={(id) => run('default', 'mutation D($id:ID!){chooseDefaultPaymentMethod(paymentMethodExternalId:$id){externalId}}', { id })}
      onForget={(id) => run('forget', 'mutation F($id:ID!){forgetPaymentMethod(paymentMethodExternalId:$id){externalId}}', { id })}
    />

    <ChargesPanel
      charges={data?.scheduledCharges ?? []}
      busy={busy}
      onPayNow={payNow}
    />
    <StatementPanel />
    <PlansPanel
      plans={data?.planOptions ?? []}
      currentCode={summary?.planCode}
      subscription={data?.subscription ?? null}
      busy={busy}
      onSubscribe={(code) => run('plan', 'mutation S($c:String!){subscribeToPlan(planCode:$c){planCode}}', { c: code })}
      onChange={(code) => run('plan', 'mutation C($c:String!){changePlan(planCode:$c){pendingPlanCode}}', { c: code })}
      onCancel={() => run('plan', 'mutation { cancelPlan(atPeriodEnd: true) { cancelAtPeriodEnd } }')}
    />
  </>;
}

function PromotionPanel({ busy, onRedeem }: { busy: string; onRedeem: (code: string) => Promise<boolean> }) {
  const [code, setCode] = useState('');
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedCode = code.trim();
    if (!normalizedCode) return;
    if (await onRedeem(normalizedCode)) setCode('');
  }
  return <section className="workspace-section">
    <div className="section-heading"><div><h2>Promotion code</h2><p>Redeem a promotion for notice credits. Credits use the same order and billing workflow and cover the next eligible notice charge.</p></div></div>
    <form className="inline-card" onSubmit={(event) => void submit(event)}>
      <label>Promotion code<input name="code" required autoComplete="off" value={code} onChange={(event) => setCode(event.target.value)} /></label>
      <button className="button" disabled={busy !== ''}>{busy === 'promotion' ? 'Redeeming…' : 'Redeem code'}</button>
    </form>
  </section>;
}

function CardsPanel(props: {
  methods: PaymentMethod[]; busy: string; onReload: () => void; onCardAdded: () => void;
  onChooseDefault: (id: string) => void; onForget: (id: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  return <section className="workspace-section">
    <div className="section-heading">
      <div><h2>Cards on file</h2><p>The default card is charged when a notice becomes due.</p></div>
      <button className="button button-quiet" onClick={() => setAdding((open) => !open)}>{adding ? 'Cancel' : 'Add a card'}</button>
    </div>

    {props.methods.length === 0 && !adding && <div className="empty-state">
      <h2>No card on file</h2>
      <p>Add one so notices can be paid for before their deadline.</p>
    </div>}

    {props.methods.length > 0 && <div className="data-table" tabIndex={0}>
      <table>
        <thead><tr><th>Card</th><th>Expires</th><th>Default</th><th /></tr></thead>
        <tbody>
          {props.methods.map((method) => <tr key={method.externalId}>
            <td>{title(method.brand)} ending {method.last4}</td>
            <td>{String(method.expiryMonth).padStart(2, '0')}/{method.expiryYear}</td>
            <td>{method.isDefault
              ? <span className="status">Default</span>
              : <button className="text-link" onClick={() => props.onChooseDefault(method.externalId)} disabled={props.busy !== ''}>Make default</button>}</td>
            <td><button className="text-link" onClick={() => props.onForget(method.externalId)} disabled={props.busy !== ''}>Remove</button></td>
          </tr>)}
        </tbody>
      </table>
    </div>}

    {adding && <AddCard onDone={() => { setAdding(false); props.onCardAdded(); }} />}
  </section>;
}

/**
 * Collects a card. With a publishable key the card goes straight to the gateway
 * and never reaches PayRight's servers; without one the development rail
 * registers a card itself, so the rest of the flow can be exercised locally.
 */
function AddCard({ onDone }: { onDone: () => void }) {
  const { token } = useAuth();
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState('');
  const stripe = useMemo(() => stripeClient(), []);
  // Starting setup twice mints a second intent and strands the first. React
  // runs effects twice under StrictMode, and a remount would do it again, so
  // the guard is a ref rather than a dependency list.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    void graphql<{ startCardSetup: { clientSecret: string } }>({
      query: 'mutation { startCardSetup { externalId clientSecret } }', token, organizationId: activeOrganizationId()
    }).then(async (value) => {
      setClientSecret(value.startCardSetup.clientSecret);
      if (!stripe) {
        // No gateway in development: the rail already holds the card, so pull
        // it through and finish.
        await graphql({ query: 'mutation { refreshPaymentMethods { externalId } }', token, organizationId: activeOrganizationId() });
        onDone();
      }
    }).catch((reason: Error) => setError(reason.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) return <div className="alert error" role="alert">{error}</div>;
  if (!stripe) return <p role="status">Adding a development card…</p>;
  if (!clientSecret) return <p role="status">Preparing a secure card form…</p>;

  return <Elements stripe={stripe} options={{ clientSecret }}>
    <CardForm onDone={onDone} />
  </Elements>;
}

function CardForm({ onDone }: { onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const { token } = useAuth();
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!stripe || !elements || !token) return;
    setSaving(true);
    setError('');
    const result = await stripe.confirmSetup({
      elements,
      // Stay on the page unless the card issuer demands a redirect.
      redirect: 'if_required',
      confirmParams: { return_url: `${window.location.origin}/app/billing` }
    });
    if (result.error) {
      setError(result.error.message ?? 'That card could not be saved.');
      setSaving(false);
      return;
    }
    // The gateway holds the card now; mirror it locally before showing the list.
    await graphql({ query: 'mutation { refreshPaymentMethods { externalId } }', token, organizationId: activeOrganizationId() });
    setSaving(false);
    onDone();
  }

  return <form className="inline-card" onSubmit={submit}>
    <PaymentElement />
    {error && <div className="alert error" role="alert">{error}</div>}
    <button className="button" disabled={!stripe || saving}>{saving ? 'Saving…' : 'Save card'}</button>
  </form>;
}

function ChargesPanel({ charges, busy, onPayNow }: { charges: Charge[]; busy: string; onPayNow: (id: string) => void }) {
  if (charges.length === 0) return null;
  return <section className="workspace-section">
    <div className="section-heading"><div>
      <h2>Notice charges</h2>
      <p>Each notice is charged ahead of its deadline, leaving time to fix a payment problem.</p>
    </div></div>
    <div className="data-table" tabIndex={0}>
      <table>
        <thead><tr><th>Notice</th><th>Amount</th><th>Charged from</th><th>Deadline</th><th>Status</th><th /></tr></thead>
        <tbody>
          {charges.map((charge) => <tr key={charge.id}>
            <td>{title(charge.description)}</td>
            <td>{money(charge.amountCents)}</td>
            <td>{calendarDate(charge.dueAt)}</td>
            <td>{charge.deadlineAt ? calendarDate(charge.deadlineAt) : '—'}</td>
            <td>
              <span className="status">{title(charge.status)}</span>
              {charge.lastFailureMessage && <small>{charge.lastFailureMessage}</small>}
              {charge.status === 'ESCALATED' && <small>Automatic retries have stopped. Review this payment or contact support.</small>}
            </td>
            <td>
              {/* Waiting for tomorrow's sweep is no use to somebody who has just
                  fixed their card with a deadline approaching. */}
              {['FAILED', 'ESCALATED', 'REQUIRES_ACTION'].includes(charge.status) &&
                <button className="button button-small" onClick={() => onPayNow(charge.id)} disabled={busy !== ''}>
                  {busy === 'pay' ? 'Charging…' : 'Pay now'}
                </button>}
            </td>
          </tr>)}
        </tbody>
      </table>
    </div>
  </section>;
}

/** A period statement, with the job on every line so it can be cost-coded. */
function StatementPanel() {
  const { token } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const period = useMemo(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59));
    return { start: start.toISOString(), end: end.toISOString() };
  }, []);

  async function download() {
    if (!token) return;
    setDownloading(true);
    setError('');
    try {
      const result = await graphql<{ statementDownload: string }>({
        query: 'query S($s:String!,$e:String!,$f:String!){ statementDownload(periodStart:$s,periodEnd:$e,format:$f) }',
        variables: { s: period.start, e: period.end, f: 'csv' },
        token, organizationId: activeOrganizationId()
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([result.statementDownload], { type: 'text/csv' }));
      link.download = `payright-statement-${period.start.slice(0, 7)}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  return <section className="workspace-section">
    <div className="section-heading">
      <div><h2>This month's statement</h2><p>One line per notice, with the job reference for your cost coding.</p></div>
      <button className="button button-quiet" onClick={download} disabled={downloading}>
        {downloading ? 'Preparing…' : 'Download spreadsheet'}
      </button>
    </div>
    {error && <div className="alert error" role="alert">{error}</div>}
  </section>;
}

function PlansPanel(props: {
  plans: Plan[]; currentCode?: string; subscription: Subscription | null; busy: string;
  onSubscribe: (code: string) => void; onChange: (code: string) => void; onCancel: () => void;
}) {
  const { plans, currentCode, subscription, busy } = props;
  if (plans.length === 0) return null;
  const pending = subscription?.pendingPlanCode;
  return <section className="workspace-section">
    <div className="section-heading">
      <div>
        <h2>Plans</h2>
        <p>Plan changes take effect at the start of your next billing period, so you are never charged twice for one month.</p>
      </div>
      {subscription && !subscription.cancelAtPeriodEnd &&
        <button className="button button-quiet" onClick={props.onCancel} disabled={busy !== ''}>Cancel plan</button>}
    </div>

    {subscription?.cancelAtPeriodEnd && <div className="alert" role="status">
      Your plan ends on {momentDate(subscription.currentPeriodEnd)}. You keep everything until then.
    </div>}
    {pending && <div className="alert" role="status">
      Switching to {plans.find((plan) => plan.code === pending)?.name ?? pending} on {momentDate(subscription!.currentPeriodEnd)}.
    </div>}
    {subscription?.status === 'PAST_DUE' && <div className="alert error" role="alert">
      We could not collect your renewal. Add a working card to keep your plan.
    </div>}
    <div className="data-table" tabIndex={0}>
      <table>
        <thead><tr><th>Plan</th><th>Monthly</th><th>Included notices</th><th>Users</th><th /></tr></thead>
        <tbody>
          {plans.map((plan) => <tr key={plan.code}>
            <td>{plan.name}</td>
            <td>{plan.monthlyPriceCents === 0 ? (plan.selfServe ? 'Free' : 'Contact us') : `${money(plan.monthlyPriceCents)}/mo`}</td>
            <td>{plan.includedNoticeCredits}</td>
            <td>{plan.maxUsers ?? 'No limit'}</td>
            <td>
              {plan.code === currentCode
                ? <span className="status">Current plan</span>
                : !plan.selfServe
                  ? <span className="status">Talk to us</span>
                  : <button
                      className="button button-small"
                      disabled={busy !== '' || plan.code === pending}
                      onClick={() => (subscription ? props.onChange(plan.code) : props.onSubscribe(plan.code))}
                    >{plan.code === pending ? 'Scheduled' : subscription ? 'Switch' : 'Choose'}</button>}
            </td>
          </tr>)}
        </tbody>
      </table>
    </div>
  </section>;
}
